import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Button } from '@m/alchemy-ui/Button';
import { DateRangePicker } from '@m/alchemy-ui/DateRangePicker';
import { EmptyState as ErrorMessage } from '@m/alchemy-ui/esm/empty-state/EmptyState';
import { LoadingIndicator } from '@m/alchemy-ui/LoadingIndicator';
import { Select } from '@m/alchemy-ui/Select';
import { Toast } from '@m/alchemy-ui/Toast';
import { BarChart } from '@m/alchemy-ui/esm/visualizations/bar-chart/BarChart';
import { useIntl } from 'react-intl';
import { ThemeProvider } from '@m/alchemy-ui/theme';
import { getToastMessages } from '../../helpers';
import {
  type ChallengesQuery,
  type CoauthorIdeasQuery,
  type FilteredUsersQuery,
  GET_CHALLENGES,
  GET_COAUTHOR_IDEAS,
  GET_COUNTRIES,
  GET_IDEAS_AND_COUNT,
  GET_USERFIELDS_FILTERED_USERIDS,
  type GetCountriesQuery,
  type GetDailySummaryQuery,
  type GetIdeasAndCountQuery
} from '../../graphql/queries';
import { useUserFields, useWindowWidth } from '../../hooks';
import { CommunityContext } from '../../context';
import { GET_DAILY_SUMMARY } from '../../graphql/queries/stat';
import { ToastLevel, type ToastMessageType, ToastPlacement } from '../../common/enums';
import {
  ChartActionsRow,
  ChartScroll,
  ChartScrollInner,
  Grid,
  GridCol,
  GridHeader,
  GridRow,
  Pagination
} from './Analytics.styled';
import * as Styled from './components/SearchFilters.styled';
import { CustomFilters } from './CustomFilters';
import { type UserFieldType } from './types';
import { ChartExportMenu, GranularitySelector, IdeasTable, TableView } from './components';
import { EXPORT_TO_CSV_ENDPOINT } from './constants';
import { ChallengeSelectionType } from './enums';
import { aggregateToChartSeries, type DateGranularity } from './helpers';
import { accessibleChartTheme, getChartMinWidth, getChartScrollWidth } from './helpers/chartUtils';

export const IdeaStats = ({ dataTableViewEnabled }: { dataTableViewEnabled: boolean }) => {
  const intl = useIntl();
  const community = useContext(CommunityContext);
  const { data: communityUserFields } = useUserFields();

  const [endDate, setEndDate] = useState<Date | null>(() => new Date());
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date;
  });
  const [clickDate, setClickDate] = useState<Date | undefined>();
  const [clickEndDate, setClickEndDate] = useState<Date | undefined>();
  const [granularity, setGranularity] = useState<DateGranularity>('day');
  const [chartUpdateKey, setChartUpdateKey] = useState<number>(0);

  const defaultUserFieldOptions: UserFieldType = {};
  const [userFieldOptions, setUserFieldOptions] = useState(defaultUserFieldOptions);

  const [countries, setCountries] = useState<string[]>([]);
  const [challenge, setChallenge] = useState<string>('allPublishedChallenges');
  const [chartData, setChartData] = useState<GetDailySummaryQuery>({ dailySummary: [] });
  const [sort, setSort] = useState<string[][]>([['createdAt', 'DESC']]);
  const [offset, setOffset] = useState<number>(0);
  const ideasLabel = intl.formatMessage({ id: 'ideas', defaultMessage: 'Ideas' });

  const [messages, setMessages] = useState<ToastMessageType[]>();

  const { data: countriesList } = useQuery<GetCountriesQuery>(GET_COUNTRIES, {
    variables: { id: community.id },
    fetchPolicy: 'cache-first'
  });

  const windowWidth = useWindowWidth();
  const chartScrollWidth = useMemo(() => getChartScrollWidth(windowWidth), [windowWidth]);

  useEffect(() => {
    if (countriesList?.community) {
      countriesList.community && setCountries(countriesList.community.countries as string[]);
    }
  }, [countriesList]);

  const userFields = JSON.stringify(userFieldOptions);

  const [ideasQueryVariables, setIdeasQueryVariables] = useState({
    challengeSelection: challenge,
    filters: { userFields },
    startDate,
    endDate,
    order: sort,
    offset: offset * 10
  });

  useEffect(() => {
    setIdeasQueryVariables({
      challengeSelection: challenge,
      filters: { userFields },
      startDate,
      endDate,
      order: sort,
      offset: offset * 10
    });
  }, [challenge, setIdeasQueryVariables, userFields, startDate, endDate, sort, offset]);

  const [ideasAndCount, setIdeasAndCount] = useState<GetIdeasAndCountQuery['ideasAndCount']>();

  const [getIdeasData, { loading: loadingIdeas }] = useLazyQuery<GetIdeasAndCountQuery>(GET_IDEAS_AND_COUNT, {
    variables: ideasQueryVariables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    onCompleted: (response) => {
      setIdeasAndCount(response?.ideasAndCount);
    }
  });

  const getChallengesVariables = { order: ['title'] };
  const { loading: loadingChallenges, data: challengeList } = useQuery<ChallengesQuery>(GET_CHALLENGES, {
    variables: getChallengesVariables,
    fetchPolicy: 'cache-first'
  });

  const dailySummaryQueryVariables = {
    challengeSelection: challenge,
    filters: { userFields },
    // Use toDateString because we don't care about time,
    // and the absence of a timezone is fine because we treat everything as GMT in charts currently
    startDate: startDate ? startDate.toDateString() : undefined
  };
  const initialQueryVariables = useRef({
    dailySummary: dailySummaryQueryVariables,
    ideas: ideasQueryVariables
  });

  const [getChartData, { loading: loadingChart, data: fetchedChartData }] = useLazyQuery<GetDailySummaryQuery>(
    GET_DAILY_SUMMARY,
    {
      variables: dailySummaryQueryVariables,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
      onCompleted: (response) => {
        setChartData(response);
      }
    }
  );

  const [getFilteredUserIds, { data: customFiltersUserIds }] = useLazyQuery<FilteredUsersQuery>(
    GET_USERFIELDS_FILTERED_USERIDS,
    {
      variables: { filters: { userFields } },
      onCompleted: (response) => {
        getCoauthorIdeaIds({
          variables: {
            filters: { userFields },
            customFiltersUserIds: response?.filteredUsers || []
          }
        });
      }
    }
  );

  const [getCoauthorIdeaIds, { data: coauthorIdeaIds }] = useLazyQuery<CoauthorIdeasQuery>(GET_COAUTHOR_IDEAS, {
    variables: { customUserFieldsIds: customFiltersUserIds, filters: { userFields } }
  });

  useEffect(() => {
    if (fetchedChartData?.dailySummary && !startDate && !endDate) {
      setStartDate(new Date(fetchedChartData?.dailySummary[0]?.created || Date.now()));
      setEndDate(
        new Date(fetchedChartData?.dailySummary[fetchedChartData.dailySummary.length - 1]?.created || Date.now())
      );
    }
  }, [chartData, fetchedChartData, startDate, endDate]);

  const lastUserFieldOptions = useRef<UserFieldType>(userFieldOptions);
  const lastChallenge = useRef<string>(challenge);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    lastUserFieldOptions.current = userFieldOptions;
    lastChallenge.current = challenge;
  }, [userFieldOptions, challenge]);

  const chartSeriesData = useMemo(() => {
    let dailyData = chartData?.dailySummary || [];

    // Filter out data beyond the selected end date
    if (endDate && dailyData.length > 0) {
      // Add one day to endDate to make it inclusive (filter anything after end of endDate)
      const dayAfterEnd = new Date(endDate);
      dayAfterEnd.setDate(dayAfterEnd.getDate() + 1);
      dayAfterEnd.setHours(0, 0, 0, 0);

      // Filter to only include entries where created < start of day after endDate
      dailyData = dailyData.filter((item) => {
        if (!item.created) return false;
        return item.created < dayAfterEnd.getTime();
      });
    }

    return aggregateToChartSeries(dailyData, granularity, intl);
  }, [chartData, granularity, intl, endDate]);

  const barChartData = useMemo(
    () => [
      {
        id: ideasLabel,
        data: chartSeriesData
      }
    ],
    [chartSeriesData, ideasLabel]
  );

  const chartMinWidth = useMemo(() => getChartMinWidth(chartSeriesData.length), [chartSeriesData.length]);

  const ideasSort = (column: string) => {
    const [sortColumn, sortDirection] = sort[0];
    const newOrder = [[column, column === sortColumn && sortDirection === 'ASC' ? 'DESC' : 'ASC']];
    getIdeasData({
      variables: {
        ...ideasQueryVariables,
        ...(clickDate && { startDate: clickDate, endDate: clickDate }),
        order: newOrder
      }
    });
    setSort(newOrder);
  };

  const getColumnSortDirection = (column: string) => {
    const [sortColumn, sortDirection] = sort[0];
    return sortColumn === column ? (sortDirection === 'DESC' ? 'descending' : 'ascending') : 'none';
  };

  const handleUpdateClick = () => {
    setOffset(0);
    if (clickDate) {
      setClickDate(undefined);
      setIdeasQueryVariables({ ...ideasQueryVariables, offset: 0, startDate, endDate });
    }

    setChartUpdateKey((prev) => prev + 1);
    getChartData({ variables: dailySummaryQueryVariables });
    getIdeasData({ variables: { ...ideasQueryVariables, offset: 0, startDate, endDate } });
    getFilteredUserIds({ variables: { filters: { userFields } } });
  };

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  const tableColumns = useMemo(
    () => [
      { label: intl.formatMessage({ id: 'date', defaultMessage: 'Date' }) },
      { label: intl.formatMessage({ id: 'ideas', defaultMessage: 'Ideas' }) }
    ],
    [intl]
  );

  const buildTableRows = useCallback(() => {
    return (
      chartData.dailySummary
        ?.filter((item) => item.created)
        .map((item) => [new Date(item.created as number), item.total]) || []
    );
  }, [chartData.dailySummary]);

  const tableRows = useMemo(() => {
    if (!dataTableViewEnabled) {
      return [];
    }

    return buildTableRows();
  }, [buildTableRows, dataTableViewEnabled]);

  const buildExportRows = useCallback(() => {
    return chartSeriesData.map((point) => [point.label, point.y]);
  }, [chartSeriesData]);

  const getExportData = useCallback(() => {
    return { columns: tableColumns, rows: buildExportRows() };
  }, [buildExportRows, tableColumns]);

  const handleDateSelection = (clickedDate: Date) => {
    let startDateForQuery: Date;
    let endDateForQuery: Date;

    switch (granularity) {
      case 'week': {
        const dayOfWeek = clickedDate.getUTCDay() || 7; // Monday=1, Sunday=7
        const monday = new Date(clickedDate);
        monday.setUTCDate(clickedDate.getUTCDate() - dayOfWeek + 1);
        startDateForQuery = new Date(Date.UTC(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate()));

        const sunday = new Date(startDateForQuery);
        sunday.setUTCDate(startDateForQuery.getUTCDate() + 6);
        endDateForQuery = sunday;

        break;
      }
      case 'month': {
        startDateForQuery = new Date(Date.UTC(clickedDate.getUTCFullYear(), clickedDate.getUTCMonth(), 1));
        endDateForQuery = new Date(Date.UTC(clickedDate.getUTCFullYear(), clickedDate.getUTCMonth() + 1, 0));

        break;
      }
      case 'year': {
        startDateForQuery = new Date(Date.UTC(clickedDate.getUTCFullYear(), 0, 1));
        endDateForQuery = new Date(Date.UTC(clickedDate.getUTCFullYear(), 11, 31));

        break;
      }
      default: {
        startDateForQuery = clickedDate;
        endDateForQuery = clickedDate;
      }
    }

    // Constrain to the overall chart date range
    if (startDate && startDateForQuery < startDate) {
      startDateForQuery = startDate;
    }
    if (endDate && endDateForQuery > endDate) {
      endDateForQuery = endDate;
    }

    const newIdeasQueryVariables = {
      startDate: startDateForQuery,
      endDate: endDateForQuery,
      challengeSelection: lastChallenge.current,
      filters: { userFields: JSON.stringify(lastUserFieldOptions.current) },
      order: sort,
      offset: 0
    };
    getIdeasData({ variables: newIdeasQueryVariables });
    setOffset(0);
    setClickDate(startDateForQuery);
    setClickEndDate(endDateForQuery);
    setIdeasQueryVariables(newIdeasQueryVariables);
  };

  const handleTableRowClick = (index: number) => {
    const row = tableRows[index];
    const dateCell = row?.[0];
    if (!(dateCell instanceof Date)) {
      return;
    }

    handleDateSelection(dateCell);
  };

  useEffect(() => {
    getChartData({ variables: initialQueryVariables.current.dailySummary });
    getIdeasData({ variables: initialQueryVariables.current.ideas });
  }, [getChartData, getIdeasData]);

  if (loadingChallenges && !challengeList) {
    return (
      <Styled.LoadingContainer>
        <LoadingIndicator inline />
      </Styled.LoadingContainer>
    );
  }

  return (
    <Grid key="ideaStats">
      <CustomFilters
        userFieldOptions={userFieldOptions}
        communityUserFields={communityUserFields}
        countries={countries}
        userFieldUpdate={setUserFieldOptions}
      />
      <GridHeader key="challengeSelectionHeader">
        {intl.formatMessage({ id: 'show', defaultMessage: 'Show' })}
      </GridHeader>
      <GridRow key="challengeSelection">
        <Select
          defaultValue={challenge}
          options={[
            {
              label: intl.formatMessage({ id: 'allPublishedChallenges', defaultMessage: 'All Published Challenges' }),
              value: ChallengeSelectionType.PUBLISHED,
              'data-testid': `challenge-${ChallengeSelectionType.PUBLISHED}`
            },
            {
              label: intl.formatMessage({ id: 'allChallenges', defaultMessage: 'All Challenges' }),
              value: ChallengeSelectionType.ALL,
              'data-testid': `challenge-${ChallengeSelectionType.ALL}`
            },
            ...((challengeList as ChallengesQuery) &&
              (challengeList?.challenges as { title: string; id: string }[]) &&
              (challengeList?.challenges as { title: string; id: string }[]).map(
                (challenge: { title: string; id: string }) => ({
                  label: challenge.title,
                  value: challenge.id,
                  'data-testid': `challenge-${challenge.title}`
                })
              ))
          ]}
          onChange={(e) => {
            setChallenge(e.value);
          }}
        />
      </GridRow>
      <GridHeader key="rangeSelectionHeader">
        {intl.formatMessage({ id: 'dateRange', defaultMessage: 'Date range' })}
      </GridHeader>
      {loadingChart && !fetchedChartData ? (
        <Styled.LoadingContainer>
          <LoadingIndicator inline />
        </Styled.LoadingContainer>
      ) : (
        <GridRow key="rangeSelection">
          <DateRangePicker
            start={{
              date: startDate,
              onDateChange: (date: Date | null) => {
                setStartDate(date ? date : startDate);
              }
            }}
            end={{
              date: endDate,
              onDateChange: (date: Date | null) => {
                setEndDate(date ? date : endDate);
              }
            }}
          />
        </GridRow>
      )}
      <GridRow key="updateButton">
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <Button priority="secondary" onClick={handleUpdateClick}>
            {intl.formatMessage({ id: 'update', defaultMessage: 'Update' })}
          </Button>
          <GranularitySelector granularity={granularity} onChange={setGranularity} />
        </div>
      </GridRow>
      {!dataTableViewEnabled && (
        <ChartActionsRow key="ideaStatsExport">
          <ChartExportMenu
            chartRef={chartContainerRef}
            fileName={intl.formatMessage({ id: 'ideaStats', defaultMessage: 'Idea Stats' })}
            formatDate={formatDate}
            getExportData={getExportData}
          />
        </ChartActionsRow>
      )}
      <GridRow key="chart" data-testid="chart-ideas-by-tag">
        {dataTableViewEnabled ? (
          <TableView
            columns={tableColumns}
            rows={tableRows}
            formatDate={formatDate}
            onRowClick={handleTableRowClick}
            summary={intl.formatMessage({ id: 'ideaStats', defaultMessage: 'Idea Stats' })}
          />
        ) : (
          <ChartScroll width={chartScrollWidth}>
            <ChartScrollInner minWidth={chartMinWidth} ref={chartContainerRef}>
              <ThemeProvider theme={accessibleChartTheme}>
                <BarChart
                  key={`chart-${granularity}-${chartUpdateKey}`}
                  data={barChartData}
                  hasLegend={false}
                  hasValueLabels
                  chartSizerProps={{ minHeight: 450 }}
                  axisX={{
                    formatTick: (value) => {
                      const index = Number(value);
                      if (!Number.isFinite(index) || !chartSeriesData[index]) {
                        return '';
                      }
                      return chartSeriesData[index].shortLabel;
                    },
                    formatTooltipValue: (value) => {
                      const index = Number(value);
                      return Number.isFinite(index) && chartSeriesData[index] ? chartSeriesData[index].label : value;
                    }
                  }}
                  axisY={{
                    minTickInterval: 1,
                    formatScreenReaderValue: (value) => {
                      return `${ideasLabel}: ${value}`;
                    }
                  }}
                  onDataClick={(payload) => {
                    const index = Number(payload?.x);
                    if (!Number.isFinite(index) || !chartSeriesData[index]) {
                      return;
                    }

                    const point = chartSeriesData[index];
                    handleDateSelection(new Date(point.created));
                  }}
                />
              </ThemeProvider>
            </ChartScrollInner>
          </ChartScroll>
        )}
      </GridRow>
      <GridRow key="table" justify="center">
        {loadingIdeas && !ideasAndCount ? (
          <Styled.LoadingContainer>
            <LoadingIndicator inline />
          </Styled.LoadingContainer>
        ) : ideasAndCount?.count ? (
          <>
            <GridCol>
              <IdeasTable
                getColumnSortDirection={getColumnSortDirection}
                ideasAndCount={ideasAndCount || { count: 0, rows: [] }}
                intl={intl}
                sortIdeas={ideasSort}
              />
            </GridCol>
            <Pagination
              numPages={Math.ceil(ideasAndCount.count / 10)}
              activeIndex={offset}
              onPageClick={(page) => {
                setOffset(page.index as number);
                getIdeasData({
                  variables: {
                    ...ideasQueryVariables,
                    ...(clickDate && clickEndDate && { startDate: clickDate, endDate: clickEndDate }),
                    offset: (page.index as number) * 10
                  }
                });
              }}
            />
          </>
        ) : (
          <ErrorMessage
            message={intl.formatMessage({ id: 'noIdeasWereFound', defaultMessage: 'No ideas were found' })}
            details={intl.formatMessage({
              id: 'pleaseAdjustYourSearch',
              defaultMessage: 'Please adjust your search parameters and try again.'
            })}
          />
        )}
      </GridRow>
      <GridRow key="exportData">
        <Button
          priority="secondary"
          onClick={() => {
            const includeUnpublished = challenge === 'allPublishedChallenges' ? 0 : 1;
            const challengeId =
              challenge === 'allPublishedChallenges' || challenge === 'allChallenges' ? '0' : challenge;

            const exportUrl = new URL(EXPORT_TO_CSV_ENDPOINT + challengeId.toString(), `https://${community.url}`);
            exportUrl.searchParams.append('includeUnpublished', includeUnpublished.toString());
            exportUrl.searchParams.append('includeComments', '0');
            exportUrl.searchParams.append(
              'fromDate',
              ((clickDate || startDate || new Date()).valueOf() / 1000).toString()
            );
            exportUrl.searchParams.append(
              'toDate',
              ((clickEndDate || endDate || new Date()).valueOf() / 1000).toString()
            );

            if (Object.keys(userFieldOptions).length > 0) {
              exportUrl.searchParams.append(
                'customFiltersUserIds',
                (customFiltersUserIds?.filteredUsers || []).toString()
              );

              exportUrl.searchParams.append('ideaIds', (coauthorIdeaIds?.coauthorIdeas || []).toString());
            }

            if (ideasAndCount?.count === 0) {
              setMessages(
                getToastMessages(
                  ToastLevel.INFORMATIVE,
                  intl.formatMessage({ id: 'noIdeasToExport', defaultMessage: 'There are no ideas to export' }),
                  ToastPlacement.BOTTOM
                )
              );
              return;
            }

            window.location = exportUrl as unknown as Location;
          }}
        >
          {intl.formatMessage({ id: 'exportData', defaultMessage: 'Export Data' })}
        </Button>
      </GridRow>
      <Toast messages={messages} />
    </Grid>
  );
};
